import Button from '@jetbrains/ring-ui/components/button/button'
import Confirm from '@jetbrains/ring-ui/components/confirm/confirm'
import Dropdown from '@jetbrains/ring-ui/components/dropdown/dropdown'
import Tag from '@jetbrains/ring-ui/components/tag/tag'
import Toggle from '@jetbrains/ring-ui/components/toggle/toggle'
import classNames from 'classnames'
import type {SyntheticEvent} from 'react'
import * as React from 'react'
import {Suspense} from 'react'

import {fetchProjectWithAllParentsData} from '../../../../../../../actions/projects'
import {getBuildTypeArg} from '../../../../../../../rest/buildTypes'
import {
  getBuildType,
  getFullPath,
  getProject,
  isBuildTypeLoaded,
  isProjectLoaded,
} from '../../../../../../../selectors'
import {restApi} from '../../../../../../../services/rest'
import {getProjectBuildTypeFilter, ROOT_PROJECT_ID} from '../../../../../../../types'
import type {BuildTypeId, ProjectId} from '../../../../../../../types'
import {notNull} from '../../../../../../../utils/guards'
import type {KeyValue} from '../../../../../../../utils/object'
import {getItemKey, SEPARATOR} from '../../../../../../common/BuildPath/BuildPath'
import pathStyles from '../../../../../../common/BuildPath/BuildPath.css'
import IconButton from '../../../../../../common/IconButton/IconButton'
import Popup from '../../../../../../common/Popup/Popup.lazy'
import ProjectOrBuildTypeIcon from '../../../../../../common/ProjectOrBuildTypeIcon/ProjectOrBuildTypeIcon'
import {EntityIconType} from '../../../../../../common/ProjectOrBuildTypeIcon/ProjectOrBuildTypeIcon.types'
import type {CleanupProjectPageUseIsReadOnlyFragment$key} from '../../../../__generated__/CleanupProjectPageUseIsReadOnlyFragment.graphql'
import {useIsReadOnly} from '../../../../CleanupProjectPage.hooks'
import {shouldPreventClickOnRow} from '../../../Listing.utils'
import type {CleanupHolderNode, RuleId, Rule as RuleType} from '../../Rules.types'
import {getRuleHolderIds, getRuleOwnership, isRuleOverridesTemplate} from '../../Rules.utils'

import ParentLink from './ParentLink'
import {
  getIfRuleKeepsEverything,
  getRemoveActionDescription,
  getWhatToSaveString,
} from './RuleItem.utils'

import styles from './RuleItem.css'

import {useAppSelector, useAppDispatch} from 'src/hooks/react-redux'

type ListItemProps = {
  holderNode: CleanupHolderNode
  disableButtons?: boolean
  rule: RuleType
  projectId: ProjectId
  onRuleOpenClick: (arg0: RuleId) => unknown
  onRuleDeleteClick: (arg0: RuleId) => unknown
  onRuleDisableClick: (arg0: RuleType) => unknown
  readOnlyProjectsKey: CleanupProjectPageUseIsReadOnlyFragment$key | null
}
const DICTIONARY: KeyValue<string, KeyValue<string, string>> = {
  personal: {
    not_personal: 'Not\u00a0personal',
    personal: 'Personal',
  },
  buildStatus: {
    successful: 'Successful',
    failed_to_start: 'Failed to\u00a0start',
    failed: 'Failed',
  },
  branchStatus: {
    inactive: 'Inactive',
    active: 'Active',
  },
}

function InheritanceSection({rule}: {rule: RuleType}) {
  const {holder} = rule
  const type = holder === 'project' ? EntityIconType.PROJECT : EntityIconType.TEMPLATE
  const dispatch = useAppDispatch()
  const {buildTypeId, projectId} = getRuleHolderIds(rule)
  const projectLoaded = useAppSelector(state => isProjectLoaded(state, projectId))
  const project = useAppSelector(state => getProject(state, projectId))
  const templateLoaded = useAppSelector(state => isBuildTypeLoaded(state, buildTypeId))
  const template = useAppSelector(state => getBuildType(state, buildTypeId))
  const fullPath = useAppSelector(state =>
    getFullPath(
      state,
      getProjectBuildTypeFilter({
        projectId,
        buildTypeId,
      }),
    ),
  )
  React.useEffect(() => {
    if (projectId != null && !project && !projectLoaded) {
      return dispatch(fetchProjectWithAllParentsData(projectId)).unsubscribe
    }
    return undefined
  }, [dispatch, project, projectId, projectLoaded])

  const [fetchSingleBuildTypeData] =
    restApi.endpoints.getBuildTypeNormalized.useLazyQuerySubscription()
  React.useEffect(() => {
    if (buildTypeId != null && !template && !templateLoaded) {
      fetchSingleBuildTypeData(getBuildTypeArg(buildTypeId))
    }
  }, [buildTypeId, fetchSingleBuildTypeData, template, templateLoaded])
  const parentName = project?.name ?? template?.name
  return (
    <div>
      {parentName != null && (
        <Dropdown
          className={styles.parent}
          hoverMode
          hoverShowTimeOut={0}
          anchor={
            <ParentLink projectId={projectId} aria-label={parentName}>
              {() => <ProjectOrBuildTypeIcon className={styles.icon} type={type} size="S" />}
            </ParentLink>
          }
        >
          <Popup className={styles.parentPopup}>
            {projectId === ROOT_PROJECT_ID
              ? parentName
              : fullPath.map((item, i) => {
                  const props =
                    'parentProjectId' in item
                      ? {
                          projectId: item.id as any as ProjectId,
                        }
                      : {
                          buildTypeId: item.id as any as BuildTypeId,
                        }
                  return (
                    <React.Fragment key={getItemKey(item)}>
                      {i > 0 && <span className={pathStyles.separator}>{SEPARATOR}</span>}
                      <ParentLink {...props}>{item.name}</ParentLink>
                    </React.Fragment>
                  )
                })}
          </Popup>
        </Dropdown>
      )}
    </div>
  )
}

type ActionsSectionProps = {
  projectId: ProjectId
  holderNode: CleanupHolderNode
  disableButtons?: boolean
  onRuleOpenClick: (ruleId: string) => void
  onRuleDeleteClick: (ruleId: string) => void
  onRuleDisableClick: (rule: RuleType) => void
  rule: RuleType
  readOnlyProjectsKey: CleanupProjectPageUseIsReadOnlyFragment$key | null
}
function ActionsSection({
  projectId,
  holderNode,
  disableButtons,
  onRuleOpenClick,
  onRuleDeleteClick,
  onRuleDisableClick,
  rule,
  readOnlyProjectsKey,
}: ActionsSectionProps) {
  const readOnly = useIsReadOnly(projectId, readOnlyProjectsKey)
  const [showConfirm, setShowConfirm] = React.useState(false)
  const openHandler = React.useCallback(
    (event: React.MouseEvent<any>) => {
      event.stopPropagation()

      if (rule.ruleId == null) {
        return
      }

      onRuleOpenClick(rule.ruleId)
    },
    [onRuleOpenClick, rule],
  )
  const ruleDisableHandle = React.useCallback(
    (event: SyntheticEvent) => {
      event.stopPropagation()
      onRuleDisableClick(rule)
    },
    [onRuleDisableClick, rule],
  )
  const deleteHandler = React.useCallback(() => {
    if (rule.ruleId == null) {
      return
    }

    onRuleDeleteClick(rule.ruleId)
    setShowConfirm(false)
  }, [onRuleDeleteClick, rule.ruleId])
  const hideDeleteConfirmation = React.useCallback(() => setShowConfirm(false), [])
  const showDeleteConfirmation = React.useCallback((event: React.SyntheticEvent) => {
    event.stopPropagation()
    setShowConfirm(true)
  }, [])
  const ruleOverridesTemplate = isRuleOverridesTemplate(rule)
  const ownership = getRuleOwnership(rule)
  const removeActionCaption = ruleOverridesTemplate ? 'Reset' : 'Delete'

  if (readOnly || ownership.enforced) {
    return (
      <Button className={styles.actionButton} disabled={disableButtons} onClick={openHandler} text>
        {'View'}
      </Button>
    )
  }

  if (ownership.own) {
    return (
      <div className={styles.actions}>
        <Button
          className={styles.actionButton}
          disabled={disableButtons}
          onClick={openHandler}
          text
        >
          {'Edit'}
        </Button>
        <Button
          className={classNames(styles.actionButton)}
          loader={disableButtons}
          onClick={showDeleteConfirmation}
          danger
          text
        >
          {removeActionCaption}
        </Button>
        <Toggle
          title={rule.ruleDisabled ? 'Enable' : 'Disable'}
          className={styles.inheritanceToggle}
          checked={!rule.ruleDisabled}
          onChange={ruleDisableHandle}
        />
        <Confirm
          show={showConfirm}
          text={`${removeActionCaption} rule for\u00a0${holderNode?.name ?? ''}`}
          description={getRemoveActionDescription(ruleOverridesTemplate)}
          onConfirm={deleteHandler}
          onReject={hideDeleteConfirmation}
        />
      </div>
    )
  }

  if (ownership.inherited && rule.ruleDisabled) {
    return (
      <div className={styles.actions}>
        <Button
          className={styles.actionButton}
          disabled={disableButtons}
          onClick={openHandler}
          text
        >
          {'View'}
        </Button>
        <Toggle
          title={rule.ruleDisabled ? 'Enable' : 'Disable'}
          className={styles.inheritanceToggle}
          checked={!rule.ruleDisabled}
          onChange={deleteHandler}
        />
      </div>
    )
  }

  if (ownership.inherited) {
    return (
      <div className={styles.actions}>
        <Button
          className={styles.actionButton}
          disabled={disableButtons}
          onClick={openHandler}
          text
        >
          {'Override'}
        </Button>
        <Toggle
          title={rule.ruleDisabled ? 'Enable' : 'Disable'}
          className={styles.inheritanceToggle}
          checked={!rule.ruleDisabled}
          onChange={showDeleteConfirmation}
        />
        <Confirm
          show={showConfirm}
          text={`Disable inherited rule for\u00a0${holderNode?.name ?? ''}`}
          description={
            <>
              {`
                You are about to\u00a0disable an\u00a0inherited keep\u00a0rule.
                Before proceeding, make sure that all valuable\u00a0data is preserved by\u00a0own keep\u00a0rules.
              `}
              <br />
              {'Click OK to\u00a0disable the\u00a0inherited\u00a0rule.'}
            </>
          }
          onConfirm={ruleDisableHandle}
          onReject={hideDeleteConfirmation}
        />
      </div>
    )
  }

  return null
}

type FiltersSectionProps = {
  rule: RuleType
}
function FiltersSection({rule}: FiltersSectionProps) {
  const tagsContent = React.useMemo(
    () =>
      rule.filters.tags?.tagsList
        .split('\n')
        .filter(Boolean)
        .map(tag => (
          <div key={tag} className={styles.tagWrapper}>
            <Tag className={styles.tag} angled>
              {tag}
            </Tag>
          </div>
        )),
    [rule.filters.tags],
  )
  const branchesActivity = rule.filters.activeBranch?.activity
  const branchesPattern = rule.filters.branchPattern?.pattern
  const chunks = [
    rule.filters.personalBuild &&
      `${DICTIONARY.personal?.[rule.filters.personalBuild.personal] ?? ''}${
        rule.filters.buildStatus ? ',' : ''
      }`,
    rule.filters.buildStatus && DICTIONARY.buildStatus?.[rule.filters.buildStatus.status],
    'Builds',
    (branchesActivity || branchesPattern) &&
      `in\u00a0${
        branchesActivity
          ? `${DICTIONARY.branchStatus?.[branchesActivity]?.toLowerCase() ?? ''}\u00a0`
          : ''
      }branches${branchesPattern != null ? `: ${branchesPattern.split('\n').join('; ')}` : ''}${
        tagsContent ? ';' : ''
      }`,
    tagsContent && 'with\u00a0tags:',
  ].filter(notNull)
  return chunks.length > 1 ? (
    <>
      {chunks.join(' ')}
      {tagsContent}
    </>
  ) : null
}

function Rule({
  rule,
  projectId,
  holderNode,
  disableButtons,
  onRuleOpenClick,
  onRuleDeleteClick,
  onRuleDisableClick,
  readOnlyProjectsKey,
}: ListItemProps) {
  const className = classNames(styles.row, {
    [styles.disabled]: rule.ruleDisabled,
    [styles.inheritedRow]: rule.source !== 'own',
  })
  const saveRow = getWhatToSaveString(rule)
  const keepsEverything = getIfRuleKeepsEverything(rule)
  const artifactPatterns = rule.keepBuildParts.artifacts?.artifactPatterns ?? null
  const onRowClick = React.useCallback(
    (event: SyntheticEvent) => {
      if (shouldPreventClickOnRow(event)) {
        return
      } else if (rule?.ruleId != null) {
        onRuleOpenClick(rule.ruleId)
      }
    },
    [onRuleOpenClick, rule],
  )
  return (
    // click handler duplicates the one of Edit button
    <div className={className} onClick={onRowClick} role="presentation">
      {rule.source !== 'own' && (
        <div className={styles.inheritance}>
          <InheritanceSection rule={rule} />
        </div>
      )}
      <div className={classNames(styles.col, styles.icons)}>
        {keepsEverything && (
          <Dropdown
            hoverMode
            hoverShowTimeOut={0}
            anchor={<IconButton special icon="warning" className={styles.warningIcon} title="" />}
          >
            <Popup className={styles.warningPopup}>
              {
                'This\u00a0rule keeps everything forever, which\u00a0makes other\u00a0rules ineffective.'
              }
            </Popup>
          </Dropdown>
        )}
        {rule.partitions.perBranch && (
          <Dropdown
            hoverMode
            hoverShowTimeOut={0}
            anchor={<IconButton icon="branches" className={styles.icon} title="" />}
          >
            <Popup className={styles.warningPopup}>
              {'This\u00a0rule applies to\u00a0each branch individually'}
            </Popup>
          </Dropdown>
        )}
      </div>
      <div className={classNames(styles.col, styles.limits)}>
        <div className={styles.filterItem}>{saveRow}</div>
      </div>
      <div className={classNames(styles.col, styles.filters)}>
        <FiltersSection rule={rule} />
      </div>
      <div className={styles.what}>
        <div className={classNames(styles.col, styles.statistics)} title={'Keep statistics'}>
          {'S'}
        </div>
        <div className={classNames(styles.col, styles.history)} title={'Keep history'}>
          {(rule.keepBuildParts.everything || rule.keepBuildParts.history) && 'H'}
        </div>
        <div className={classNames(styles.col, styles.logs)} title={'Keep logs'}>
          {(rule.keepBuildParts.everything || rule.keepBuildParts.logs) && 'L'}
        </div>
        <div className={classNames(styles.col, styles.artifacts)} title={'Keep artifacts'}>
          {(rule.keepBuildParts.everything ||
            (rule.keepBuildParts.artifacts && artifactPatterns == null)) &&
            'A'}
          {!rule.keepBuildParts.everything &&
            rule.keepBuildParts.artifacts &&
            artifactPatterns != null && (
              <Dropdown
                className={styles.artifactsDropdown}
                hoverMode
                hoverShowTimeOut={0}
                anchor={
                  <>
                    {'A'}
                    <IconButton
                      className={styles.modifiedIcon}
                      title=""
                      aria-label="Artifact patterns"
                      icon="modified-10px"
                    />
                  </>
                }
              >
                <Popup className={styles.artifactPatternsPopup}>
                  <pre className={styles.artifactPatterns}>{artifactPatterns}</pre>
                </Popup>
              </Dropdown>
            )}
        </div>
        <div
          className={classNames(styles.col, styles.dependencies)}
          title={'Keep artifact dependencies'}
        >
          {rule.preserveArtifacts === true && 'D'}
        </div>
      </div>
      <div className={classNames(styles.col, styles.actions)}>
        <Suspense>
          <ActionsSection
            rule={rule}
            projectId={projectId}
            holderNode={holderNode}
            disableButtons={disableButtons}
            onRuleOpenClick={onRuleOpenClick}
            onRuleDeleteClick={onRuleDeleteClick}
            onRuleDisableClick={onRuleDisableClick}
            readOnlyProjectsKey={readOnlyProjectsKey}
          />
        </Suspense>
      </div>
    </div>
  )
}

export default React.memo<ListItemProps>(Rule)
